import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Badge, 
  IconButton, 
  Popover, 
  List, 
  ListItem, 
  ListItemText, 
  Typography, 
  Box,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Button
} from '@mui/material';
import { 
  Notifications as NotificationsIcon,
  Event as EventIcon,
  Message as MessageIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationContext';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';

const NotificationBell = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const { 
    notifications, 
    unreadCount, 
    loading, 
    error,
    markAsRead, 
    markAllAsRead,
    clearError 
  } = useNotifications();
  const { isAuthenticated } = useBookMyOpAuth();


  const handleClick = (event) => {
    if (!isAuthenticated) {
      // Handle unauthenticated state - could show login prompt
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (error) {
      clearError();
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    
    // Handle navigation based on notification type
    if (notification.appointment) {
      navigate(`/appointments/${notification.appointment}`);
    } else if (notification.communication) {
      navigate(`/communications/${notification.communication}`);
    }
    
    handleClose();
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'appointment':
      case 'appointment_confirmed':
      case 'appointment_cancelled':
      case 'appointment_reminder':
        return <EventIcon color="primary" fontSize="small" />;
      case 'communication':
      case 'new_ticket':
      case 'ticket_reply':
        return <MessageIcon color="secondary" fontSize="small" />;
      case 'reminder':
        return <ScheduleIcon color="warning" fontSize="small" />;
      default:
        return <NotificationsIcon fontSize="small" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'appointment_confirmed':
        return 'success';
      case 'appointment_cancelled':
        return 'error';
      case 'appointment_reminder':
        return 'warning';
      case 'new_ticket':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatNotificationTime = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffInMinutes = Math.floor((now - created) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const open = Boolean(anchorEl);

  if (!isAuthenticated) {
    return (
      <IconButton color="white" onClick={handleClick}>
        <NotificationsIcon />
      </IconButton>
    );
  }

  return (
    <>
      <IconButton color="error" onClick={handleClick}>
        <Badge 
          badgeContent={unreadCount} 
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.75rem',
              fontWeight: 'bold',
              backgroundColor: '#ff4444',
              color: '#ffffff'
            }
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          style: { 
            width: 400, 
            maxHeight: 500,
            borderRadius: 12
          }
        }}
      >
        <Box p={2}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={600}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                color="primary"
                onClick={handleMarkAllAsRead}
                startIcon={<CheckCircleIcon />}
                sx={{ textTransform: 'none' }}
              >
                Mark all read
              </Button>
            )}
          </Box>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Notifications List */}
          {!loading && (
            <List sx={{ p: 0 }}>
              {notifications.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        No notifications
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        You're all caught up!
                      </Typography>
                    }
                  />
                </ListItem>
              ) : (
                notifications.slice(0, 10).map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      button
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        backgroundColor: notification.is_read ? 'transparent' : '#f8f9fa',
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': {
                          backgroundColor: notification.is_read ? '#f5f5f5' : '#e3f2fd'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                        <Box sx={{ mr: 1, mt: 0.5 }}>
                          {getNotificationIcon(notification.notification_type)}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Typography
                              variant="body2"
                              fontWeight={notification.is_read ? 'normal' : 'bold'}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {notification.content}
                            </Typography>
                            <Chip
                              label={notification.notification_type.replace('_', ' ')}
                              color={getNotificationColor(notification.notification_type)}
                              size="small"
                              sx={{ fontSize: '0.6rem', height: 20 }}
                            />
                          </Box>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {formatNotificationTime(notification.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                    {index < notifications.length - 1 && index < 9 && (
                      <Divider sx={{ my: 0.5 }} />
                    )}
                  </React.Fragment>
                ))
              )}
            </List>
          )}

          {/* View All Button */}
          {notifications.length > 0 && (
            <Box mt={2} textAlign="center">
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  handleClose();
                  navigate('/notifications');
                }}
                sx={{ textTransform: 'none' }}
              >
                View All Notifications
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell; 